/**
 * Execute step — starts the import and shows live progress.
 * Also includes B6 (E-1.B.1-06): Duplicate detection with merge/keep/skip.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import type { WizardState } from "../wizard-shell";

interface ExecuteStepProps {
  state: WizardState;
  goNext: () => void;
  updateState: (patch: Partial<WizardState>) => void;
}

interface ImportProgress {
  status: "idle" | "running" | "completed" | "failed";
  totalRecords: number;
  importedRecords: number;
  failedRecords: number;
  skippedRecords: number;
  duplicatesFound: number;
  currentPhase: string;
}

interface DuplicateCandidate {
  id: string;
  sourceRecord: Record<string, unknown>;
  existingRecord: Record<string, unknown>;
  conflictType: string;
  confidence: number;
  resolution: "pending" | "merge" | "keep_existing" | "keep_new" | "skip";
}

export function ExecuteStep({ state, updateState }: ExecuteStepProps) {
  const { t } = useTranslation();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup polling on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const [progress, setProgress] = useState<ImportProgress>({
    status: "idle",
    totalRecords: 0,
    importedRecords: 0,
    failedRecords: 0,
    skippedRecords: 0,
    duplicatesFound: 0,
    currentPhase: "",
  });
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[]>([]);
  const [showDuplicates, setShowDuplicates] = useState(false);

  const startImport = useCallback(async () => {
    if (!state.jobId) return;
    updateState({ importStatus: "running" });
    setProgress((p) => ({ ...p, status: "running", currentPhase: t("migration.execute.starting") }));

    try {
      await apiFetch(`/platform/migration/${state.jobId}/execute`, { method: "POST" });
      // Poll for progress (stored in ref for cleanup on unmount)
      pollRef.current = setInterval(async () => {
        try {
          const status = await apiFetch<ImportProgress>(`/platform/migration/${state.jobId}/status`);
          setProgress(status);
          if (status.status === "completed" || status.status === "failed") {
            if (pollRef.current) clearInterval(pollRef.current);
            updateState({ importStatus: status.status });

            // Fetch duplicates if any
            if (status.duplicatesFound > 0) {
              const dupes = await apiFetch<{ duplicates: DuplicateCandidate[] }>(
                `/platform/migration/${state.jobId}/duplicates`,
              );
              setDuplicates(dupes.duplicates);
              setShowDuplicates(true);
            }
          }
        } catch {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      }, 2000);
    } catch {
      setProgress((p) => ({ ...p, status: "failed", currentPhase: t("migration.execute.failed") }));
      updateState({ importStatus: "failed" });
    }
  }, [state.jobId, updateState, t]);

  const resolveDuplicate = (id: string, resolution: DuplicateCandidate["resolution"]) => {
    setDuplicates((prev) =>
      prev.map((d) => (d.id === id ? { ...d, resolution } : d)),
    );
  };

  const pct = progress.totalRecords > 0
    ? Math.round((progress.importedRecords / progress.totalRecords) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t("migration.execute.title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("migration.execute.description")}</p>
      </div>

      {progress.status === "idle" && (
        <div className="text-center py-8">
          <p className="mb-4 text-sm text-muted-foreground">{t("migration.execute.ready")}</p>
          <button
            onClick={startImport}
            className="rounded-md bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground"
          >
            {t("migration.execute.startButton")}
          </button>
        </div>
      )}

      {progress.status === "running" && (
        <div className="space-y-4">
          <div className="h-3 rounded-full bg-muted">
            <div className="h-3 rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-4 gap-4 text-center text-sm">
            <div>
              <p className="text-2xl font-bold text-foreground">{progress.importedRecords}</p>
              <p className="text-muted-foreground">{t("migration.execute.imported")}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{progress.totalRecords}</p>
              <p className="text-muted-foreground">{t("migration.execute.total")}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--color-warning)]">{progress.skippedRecords}</p>
              <p className="text-muted-foreground">{t("migration.execute.skipped")}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--color-destructive)]">{progress.failedRecords}</p>
              <p className="text-muted-foreground">{t("migration.execute.failed")}</p>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground">{progress.currentPhase}</p>
        </div>
      )}

      {progress.status === "completed" && (
        <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-4 text-center">
          <p className="text-lg font-semibold text-[var(--color-success)]">{t("migration.execute.completed")}</p>
          <p className="mt-1 text-sm text-[var(--color-success)]">
            {t("migration.execute.summary", {
              imported: progress.importedRecords,
              skipped: progress.skippedRecords,
              failed: progress.failedRecords,
            })}
          </p>
        </div>
      )}

      {/* B6: Duplicate detection UI */}
      {showDuplicates && duplicates.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">
            {t("migration.duplicates.title", { count: duplicates.length })}
          </h3>
          {duplicates.map((dup) => (
            <div key={dup.id} className="rounded-lg border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-[var(--color-warning)]">
                  {dup.conflictType} ({Math.round(dup.confidence * 100)}% {t("migration.duplicates.match")})
                </span>
                <div className="flex gap-1">
                  {(["merge", "keep_existing", "keep_new", "skip"] as const).map((res) => (
                    <button
                      key={res}
                      onClick={() => resolveDuplicate(dup.id, res)}
                      className={cn(
                        "rounded px-2 py-1 text-xs font-medium",
                        dup.resolution === res
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-foreground border border-border hover:bg-accent",
                      )}
                    >
                      {t(`migration.duplicates.${res}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 text-xs">
                <div>
                  <p className="font-semibold text-muted-foreground mb-1">{t("migration.duplicates.incoming")}</p>
                  {Object.entries(dup.sourceRecord).slice(0, 4).map(([k, v]) => (
                    <p key={k}><span className="text-muted-foreground">{k}:</span> {String(v)}</p>
                  ))}
                </div>
                <div>
                  <p className="font-semibold text-muted-foreground mb-1">{t("migration.duplicates.existing")}</p>
                  {Object.entries(dup.existingRecord).slice(0, 4).map(([k, v]) => (
                    <p key={k}><span className="text-muted-foreground">{k}:</span> {String(v)}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
